import {
  ArmyList,
  ArmyListUnit,
  ArmyOrganisationGroup,
  ConstrainedItem,
  ConstraintItemType,
  OrganisationPoint,
  SelectedElement,
  SelectedItem,
  SelectedItemType,
  Unit,
  UnitOption,
  UnitOptionCategory,
  UnitOptionOrganisationMode,
} from '@Models';

interface OrganisationOptionPoint extends OrganisationPoint {
  mode: UnitOptionOrganisationMode;
}

export const calculMountPointByOrganisation = (
  armyListUnit: ArmyListUnit,
  units: Array<Unit>,
): Array<OrganisationPoint> => {
  const unit = units.find((item) => item.id === armyListUnit.unit_id);
  if (!unit) return [];

  const mountOption = armyListUnit.army_list_unit_unit_options
    .filter((item) => !item.isDeleted)
    .map((item) =>
      unit.optionGroups
        .flatMap((o) => o.options)
        .find((option) => option.id === item.unit_option_id),
    )
    .filter((item): item is UnitOption => !!item)
    .filter((item) => item.category === UnitOptionCategory.Mount)
    .shift();
  if (!mountOption) return [];

  //Sum points for mounts
  const mount = units.find((item) => item.id === mountOption.mount_id);
  if (!mount) return [];

  const mountOrganisations = mount.organisationIds.filter(
    (item) => !unit.allOrganisationIds.includes(item),
  );

  return mountOrganisations.map((organisationId) => {
    let organisation: OrganisationPoint = {
      organisationId: organisationId,
      points: 0,
    } satisfies OrganisationPoint as OrganisationPoint;

    if (mountOption.mount_and_carac_points) {
      organisation.points = armyListUnit.points;
    } else {
      organisation.points = mountOption.points ?? 0;
      const mountOptionIds = mount.optionGroups
        .flatMap((o) => o.options)
        .map((item) => item.id);
      organisation.points += armyListUnit.army_list_unit_unit_options
        .filter((o) => !o.isDeleted)
        .filter((item) => mountOptionIds.includes(item.unit_option_id))
        .map((item) => item.points)
        .reduce((a: number, b: number) => a + b, 0);
    }

    return organisation;
  });
};

export const calculOptionPointByOrganisation = (
  armyListUnit: ArmyListUnit,
  units: Array<Unit>,
): Array<OrganisationPoint> => {
  const unit = units.find((item) => item.id === armyListUnit.unit_id);
  if (!unit) return [];

  const options = armyListUnit.army_list_unit_unit_options
    .filter((item) => !item.isDeleted)
    .map((item) => {
      return {
        option:
          unit.optionGroups
            .flatMap((o) => o.options)
            .find((option) => option.id === item.unit_option_id) ?? null,
        points: item.points,
      };
    })
    .filter(
      (item) => item.option !== null && item.option!!.organisation_id != null,
    )
    .map(
      (item) =>
        ({
          mode: item.option!!.organisation_mode,
          organisationId: item.option!!.organisation_id!!,
          points: item.points,
        }) satisfies OrganisationOptionPoint as OrganisationOptionPoint,
    );

  // L'option avec le mode "ChangeUnitOrganisation" est traité au niveau de l'unité car elle change d'organisation
  // L'option avec le mode "Unit" fait compter toute l'unité dans la nouvelle organisation en plus de celle d'origin
  let organisations = options
    .filter(
      (item) =>
        UnitOptionOrganisationMode.Unit === item.mode &&
        item.organisationId !== null,
    )
    .map(
      (item) =>
        ({
          organisationId: item.organisationId,
          points: armyListUnit.points,
        }) satisfies OrganisationPoint as OrganisationPoint,
    );

  // L'option avec le mode "ExcludeAddOrganisation" ne doit compter que dans l'organisation de l'unité (ex: Skin brave SA)
  options
    .filter(
      (item) => UnitOptionOrganisationMode.ExcludeAddOrganisation === item.mode,
    )
    .flatMap((item) => [
      {
        organisationId: armyListUnit.organisation_id,
        points: -item.points,
      } satisfies OrganisationPoint as OrganisationPoint,
      {
        organisationId: item.organisationId,
        points: item.points,
      } satisfies OrganisationPoint as OrganisationPoint,
    ])
    .forEach((item) => organisations.push(item));

  return organisations;
};

export const calculModelPointByOrganisation = (
  armyListUnit: ArmyListUnit,
  units: Array<Unit>,
): Array<OrganisationPoint> => {
  const unit = units.find((item) => item.id === armyListUnit.unit_id);
  if (!unit) return [];

  return unit.allOrganisationIds
    .map((originalOrganisationId) => {
      let organisations = [];
      let organisationId = originalOrganisationId;
      const armyListUnitOptionIds = armyListUnit.army_list_unit_unit_options
        .filter((item) => !item.isDeleted)
        .map((item) => item.unit_option_id);

      unit.organisation_changes
        .filter((item) => item.default_organisation_id === organisationId)
        .filter(
          (change) =>
            (change.isMin && change.number <= armyListUnit.size) ||
            (change.isMax && change.number >= armyListUnit.size),
        )
        .filter(
          (change) =>
            !change.availabilities.some((avail) =>
              armyListUnitOptionIds.some((id) => id === avail.unitOptionId),
            ),
        )
        .forEach((change) => {
          organisationId = change.new_organisation_id;
          if (change.addOrganisationId != null)
            organisations.push({
              organisationId: change.addOrganisationId,
              points: armyListUnit.points,
            } satisfies OrganisationPoint as OrganisationPoint);
        });

      const changeOptions = armyListUnit.army_list_unit_unit_options
        .filter((item) => !item.isDeleted)
        .map((item) =>
          unit.optionGroups
            .flatMap((o) => o.options)
            .find((option) => option.id === item.unit_option_id),
        )
        .filter((item): item is UnitOption => !!item)
        .filter(
          (item) =>
            item.organisation_mode ===
              UnitOptionOrganisationMode.ChangeUnitOrganisation &&
            item.organisation_id !== null,
        );
      if (
        changeOptions.length > 0 &&
        unit.principal_organisation_id === organisationId
      ) {
        const option = changeOptions.shift()!!;
        organisationId = option.organisation_id!!;
      }

      organisations.push({
        organisationId: organisationId,
        points: armyListUnit.points,
      } satisfies OrganisationPoint as OrganisationPoint);

      return organisations;
    })
    .flat();
};

export const calculPointByOrganisation = (
  armyListUnit: ArmyListUnit,
  units: Array<Unit>,
): Array<OrganisationPoint> => {
  //Sum points for mounts
  let organisations = calculMountPointByOrganisation(armyListUnit, units);

  //Sum point options with only Options
  organisations.push(...calculOptionPointByOrganisation(armyListUnit, units));

  //Sum points for all figurines
  organisations.push(...calculModelPointByOrganisation(armyListUnit, units));

  return organisations.flat();
};

export const organisationsSelectedElement = (
  armyList: ArmyList,
  armyListUnit: ArmyListUnit,
  units: Unit[],
): SelectedElement[] => {
  return calculPointByOrganisation(armyListUnit, units).map((org) => {
    return {
      type: SelectedItemType.ArmyListUnitOrganisation,
      id: org.organisationId,
      name: `Organisation ${org.organisationId}`,

      count: 1,
      quantity: armyListUnit.size,
      points: armyListUnit.points,

      armyListUnitIds: [armyListUnit.id],

      parents: [
        {
          type: SelectedItemType.ArmyListUnit,
          id: armyListUnit.id,
          name: armyListUnit.name,
        } satisfies SelectedItem as SelectedItem,
      ],

      constrained: {
        type: ConstraintItemType.Organisation,
        id: org.organisationId,
        name: `Organisation ${org.organisationId}`,
      } satisfies ConstrainedItem as ConstrainedItem,
    } satisfies SelectedElement as SelectedElement;
  });
};

export const armyListUnitSelectedElement = (
  armyList: ArmyList,
  armyListUnit: ArmyListUnit,
  units: Unit[],
): SelectedElement[] => {
  const unit = units.find((u) => u.id === armyListUnit.unit_id) ?? null;
  const unitOptionGroups = unit?.optionGroups ?? [];

  return [
    {
      type: SelectedItemType.ArmyListUnit,
      id: armyListUnit.id,
      name: armyListUnit.name,

      count: 1,
      quantity: 1,
      points: armyListUnit.points,

      armyListUnitIds: [armyListUnit.id],

      parents: [
        {
          type: SelectedItemType.ArmyList,
          id: armyList.id,
          name: armyList.name,
        } satisfies SelectedItem as SelectedItem,
      ],

      constrained: {
        type: ConstraintItemType.Unit,
        id: armyListUnit.unit_id,
        name: armyListUnit.unitName,
      } satisfies ConstrainedItem as ConstrainedItem,
    } satisfies SelectedElement as SelectedElement,
    ...organisationsSelectedElement(armyList, armyListUnit, units),
    ...unitOptionGroups.map((unitOptionGroup) => {
      const armyListUnitOptions = armyListUnit.army_list_unit_unit_options
        .filter((o) => !o.isDeleted)
        .filter((o) =>
          unitOptionGroup.options.some((ao) => o.unit_option_id === ao.id),
        );

      return {
        type: SelectedItemType.ArmyListUnitOptionGroup,
        id: unitOptionGroup.id,
        name: unitOptionGroup.name,

        count: armyListUnitOptions.length > 0 ? 1 : 0,
        quantity: armyListUnitOptions.length,
        points: armyListUnitOptions
          .map((o) => o.points)
          .reduce((a, b) => a + b, 0),

        armyListUnitIds: [armyListUnit.id],

        parents: [
          {
            type: SelectedItemType.ArmyListUnit,
            id: armyListUnit.id,
            name: armyListUnit.name,
          } satisfies SelectedItem as SelectedItem,
        ],

        constrained: {
          type: ConstraintItemType.UnitOptionGroup,
          id: unitOptionGroup.id,
          name: unitOptionGroup.name,
        } satisfies ConstrainedItem as ConstrainedItem,
      } satisfies SelectedElement as SelectedElement;
    }),
    ...armyListUnit.army_list_unit_unit_options
      .filter((o) => !o.isDeleted)
      .flatMap(
        (option) =>
          ({
            type: SelectedItemType.ArmyListUnitOption,
            id: option.id,
            name: option.name,

            count: 1,
            quantity: option.quantity,
            points: option.points,

            armyListUnitIds: [armyListUnit.id],

            parents: [
              {
                type: SelectedItemType.ArmyListUnit,
                id: armyListUnit.id,
                name: armyListUnit.name,
              } satisfies SelectedItem as SelectedItem,
            ],

            constrained: {
              type: ConstraintItemType.UnitOption,
              id: option.unit_option_id,
              name: option.name,
            } satisfies ConstrainedItem as ConstrainedItem,
          }) satisfies SelectedElement as SelectedElement,
      ),
    ...armyListUnit.troops.flatMap(
      (troop) =>
        ({
          type: SelectedItemType.ArmyListUnitTroop,
          id: troop.id,
          name: troop.name,

          count: 1,
          quantity: armyListUnit.size,
          points: armyListUnit.points,

          armyListUnitIds: [armyListUnit.id],

          parents: [
            {
              type: SelectedItemType.ArmyListUnit,
              id: armyListUnit.id,
              name: armyListUnit.name,
            } satisfies SelectedItem as SelectedItem,
          ],

          constrained: {
            type: ConstraintItemType.Troop,
            id: troop.troopId,
            name: troop.name,
          } satisfies ConstrainedItem as ConstrainedItem,
        }) satisfies SelectedElement as SelectedElement,
    ),
  ];
};

export const allSeletedElement = (
  armyList: ArmyList | null,
  armyOrganisationGroups: ArmyOrganisationGroup[],
  units: Unit[],
  ignoreArmyListUnitIds: string[] = [],
): SelectedElement[] => {
  if (armyList === null) {
    return [];
  }

  return [
    {
      type: SelectedItemType.ArmyList,
      id: armyList.id,
      name: armyList.name,

      count: 1,
      quantity: 1,
      points: armyList.points,

      armyListUnitIds: [],

      parents: [],

      constrained: {
        type: ConstraintItemType.Roster,
        id: 0,
        name: '',
      } satisfies ConstrainedItem as ConstrainedItem,
    } satisfies SelectedElement as SelectedElement,
    {
      type: SelectedItemType.ArmyOrganisation,
      id: armyList.army_organisation_id,
      name: 'Army Organisation',

      count: 1,
      quantity: 1,
      points: 0,

      armyListUnitIds: [],

      parents: [
        {
          type: SelectedItemType.ArmyList,
          id: armyList.id,
          name: armyList.name,
        } satisfies SelectedItem as SelectedItem,
      ],

      constrained: {
        type: ConstraintItemType.Army,
        id: armyList.armyId,
        name: '',
      } satisfies ConstrainedItem as ConstrainedItem,
    } satisfies SelectedElement as SelectedElement,
    ...armyOrganisationGroups.map(
      (armyOrganisationGroup) =>
        ({
          type: SelectedItemType.ArmyListUnitOrganisation,
          id: armyOrganisationGroup.organisationId,
          name: armyOrganisationGroup.name,

          count: 0,
          quantity: 0,
          points: 0,

          armyListUnitIds: [],

          parents: [],

          constrained: {
            type: ConstraintItemType.Organisation,
            id: armyOrganisationGroup.organisationId,
            name: armyOrganisationGroup.name,
          } satisfies ConstrainedItem as ConstrainedItem,
        }) satisfies SelectedElement as SelectedElement,
    ),
    ...armyList.army_list_units
      .filter(
        (armyListUnit) => armyListUnit.is_enabled && !armyListUnit.isDeleted,
      )
      .filter(
        (armyListUnit) => !ignoreArmyListUnitIds.includes(armyListUnit.id),
      )
      .flatMap((armyListUnit) =>
        armyListUnitSelectedElement(armyList, armyListUnit, units),
      ),
  ].reduce((acc, item) => {
    if (
      item.type !== SelectedItemType.ArmyListUnitOrganisation ||
      !acc.some((a) => item.id === a.id && item.type === a.type)
    ) {
      acc.push(item);
      return acc;
    }

    return acc.map((a) => {
      if (item.id === a.id && item.type === a.type) {
        return {
          ...a,
          count: a.count + item.count,
          quantity: a.quantity + item.quantity,
          points: a.points + item.points,

          armyListUnitIds: a.armyListUnitIds.concat(item.armyListUnitIds),
          parents: a.parents.concat(item.parents),
        };
      }
      return a;
    });
  }, [] as SelectedElement[]);
};
